/**
 *
 * OverallComponent
 *
 */

import React, { useContext } from 'react';
import ClassNames from 'classnames';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles } from '@material-ui/core/styles';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import PageIntroBlock from 'components/PageIntroBlock';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { getScorePercentage } from 'containers/Assessment/utils';
import AssessmentResultContext from 'containers/AssessmentResult/AssessmentResultContext';
import { useSelector } from 'react-redux';
import { makeSelectClientLanding } from 'containers/ClientLanding/selectors';
import BrowserTracker from 'utils/browserTrackerService';
import { windowScrollTo } from 'utils/embedded';
import Mixpanel, { injectAssessmentResultsABData } from 'utils/mixpanelService';
import TrendsWrapper from './Trends/TrendsWrapper';
import GaugeAboutButton from './GaugeAboutButton';
import GaugeAboutButtonWithModal from './GaugeAboutButtonWithModal';
import GaugeTrendsButton from './GaugeTrendsButton';
import GaugeProgress from './GaugeProgress';
import GaugeSignUpButton from './GaugeSignUpButton';
import ResultList from './ResultList';
import { getCurrentInterval, normalizeIntervals } from '../utils';
import RetakeButton from './RetakeButton';

const useOverallComponentStyles = makeStyles(theme => ({
  container: {
    background: '#FFFFFF',
    border: '1px solid #E3E3E3',
    borderRadius: 10,
  },
  chartWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    position: 'relative',
    padding: props => (props.isVariantB ? '30px 30px 0 30px' : 0),
  },
  linksWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    paddingBottom: 16,
    '& > button': {
      marginTop: 0,
    },
  },
  resourcesContainer: {
    width: '100%',
    marginTop: 16,
    marginBottom: 16,
  },
  infoWrapper: {
    paddingBottom: props =>
      props.invisiblePdfContent ? 16 : props.isVariantB ? 50 : 16,
    paddingTop: props =>
      props.invisiblePdfContent ? 16 : props.isVariantB ? 50 : 40,
    paddingRight: props =>
      props.invisiblePdfContent ? 16 : props.isVariantB ? 30 : 0,
    [theme.breakpoints.down('sm')]: {
      paddingTop: props => (props.isVariantB ? 10 : 0),
      paddingLeft: () => 16,
      paddingRight: () => 16,
      paddingBottom: () => 16,
    },
  },
  infoWrapperInPdfSafari: {
    paddingBottom: 40,
  },
  header: {
    ...theme.typography.h5,
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
  descriptionWrapper: {
    marginTop: 16,
  },
  descriptionLabel: {
    '& > span[role=button]': {
      cursor: 'pointer',
      marginLeft: 10,
      marginRight: 10,
      color: '#01619B',
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: 8,
      marginTop: 8,
      '& > span[role=button]': {
        marginLeft: 0,
        marginRight: 0,
      },
    },
  },
  trendsHeader: {
    marginBottom: 13,
  },
  intervalContainer: {
    paddingTop: 16,
    paddingBottom: 16,
  },
  intervalItemWrapper: {
    display: 'flex',
  },
  intervalColorWrapper: {
    height: '1.35rem',
    display: 'flex',
    alignItems: 'center',
  },
  intervalColor: {
    height: 12,
    width: 12,
    marginRight: 8,
    borderRadius: 6,
  },
  actualIntervalColor: {
    height: 14,
    width: 14,
    marginRight: 8,
    borderRadius: 7,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#FFFFFF',
  },
  intervalValues: {
    ...theme.typography.subtitle1,
    fontSize: '1rem',
    color: theme.palette.text.primary,
  },
  intervalLabel: {
    fontSize: '0.9rem',
    color: theme.palette.text.primary,
  },
  safariPdfWrapper: {
    alignItems: 'center',
  },
  signUpButtonWrapper: {
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'center',
    },
  },
  signUpButtonWrapperVariantB: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    '& > button': {
      marginLeft: 10,
      marginRight: 10,
    },
    '& > button::first-child': {
      marginLeft: 0,
    },
    '& > button::last-child': {
      marginRight: 0,
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      '& > button': {
        marginLeft: 0,
        marginRight: 0,
        marginTop: 5,
        marginBottom: 5,
      },
      '& > button::first-child': {
        marginTop: 0,
      },
      '& > button::last-child': {
        marginBottom: 0,
      },
    },
  },
}));

function OverallComponent({
  assessment,
  overallComponent,
  inCrisisComponent,
  intro,
  isFullWidth,
  hideResources,
  invisiblePdfContent,
  hideSignUpButton = false,
}) {
  const {
    fields: { name, slug },
  } = assessment;

  const { isVariantB, shouldABTest } = useContext(AssessmentResultContext);
  const [assessmentResultsSiteCopy] = useSiteCopySelector([
    'assessment-results',
  ]);
  const clientLanding = useSelector(makeSelectClientLanding());
  const userTerm = _get(clientLanding, 'userTerm') || '';

  const {
    topics,
    result,
    resources,
    overallScoring,
    maxValue,
    minValue,
    intervals = [],
    scale,
    hideChart = false,
  } = overallComponent;
  const classes = useOverallComponentStyles({
    isVariantB,
    invisiblePdfContent,
  });

  const sortedIntervals = normalizeIntervals(
    intervals.sort((a, b) => a.start - b.start),
    minValue,
    maxValue,
    scale,
  );
  const numIntervals = sortedIntervals.length;
  let intervalsCol;
  switch (numIntervals) {
    case 1:
      intervalsCol = 12;
      break;
    case 2:
      intervalsCol = 6;
      break;
    case 3:
      intervalsCol = 4;
      break;
    case 4:
      intervalsCol = 3;
      break;
    default:
      intervalsCol = 3;
      break;
  }
  const parsedIntervalsDisplay = sortedIntervals.map((interval, i) => {
    let start;
    const end = Math.round(interval.end);
    if (i === 0) {
      start = Math.round(interval.start);
    } else {
      start =
        Math.round(interval.start) === Math.round(sortedIntervals[i - 1].end)
          ? Math.round(interval.start) + 1
          : Math.round(interval.start);
    }

    return {
      start,
      end,
      color: interval.color,
      label: interval.label,
    };
  });

  const percentage = getScorePercentage(overallComponent, overallScoring);

  const [, intervalIndex] = getCurrentInterval(sortedIntervals, percentage);
  const browserName = BrowserTracker.getBrowser(
    navigator.userAgent,
    navigator.vendor,
    window.opera,
  );
  const isDownloadingPdfInSafari =
    invisiblePdfContent && browserName.includes('Safari');

  const handleMixpanelTrack = event => {
    Mixpanel.track(
      event,
      injectAssessmentResultsABData(
        {
          assessment: _get(assessment, 'fields.name'),
          entryId: _get(assessment, 'sys.id'),
        },
        shouldABTest,
        isVariantB,
      ),
    );
  };

  const handleSelfHelpResourcesClick = () => {
    const el = document.getElementById('assessment-results-user-resources');
    const topPosition = el.getBoundingClientRect().top + window.scrollY - 186;
    windowScrollTo(topPosition);

    handleMixpanelTrack('Assessment Results - Self-help Resources');
  };
  const handleMemberResourcesClick = () => {
    const el = document.getElementById('assessment-results-client-resources');
    const topPosition = el.getBoundingClientRect().top + window.scrollY - 186;
    windowScrollTo(topPosition);

    handleMixpanelTrack('Assessment Results - Member Resources');
  };

  return (
    <Grid container style={{ pageBreakInside: 'auto' }}>
      {!isVariantB && <Hidden smDown>{inCrisisComponent}</Hidden>}
      {!hideChart ? (
        <>
          <Grid item xs={12} className={classes.container}>
            <Grid
              container
              className={ClassNames({
                [classes.safariPdfWrapper]: isDownloadingPdfInSafari,
              })}
            >
              <Grid item xs={12} md="auto">
                <div className={classes.chartWrapper}>
                  <GaugeProgress
                    percentage={percentage}
                    sortedIntervals={sortedIntervals}
                    intervalIndex={intervalIndex}
                    width={270}
                    scale={scale}
                    isDownloadingPdfInSafari={isDownloadingPdfInSafari}
                  />
                </div>
                {isVariantB && !invisiblePdfContent && (intro || result) && (
                  <div className={classes.linksWrapper}>
                    {result && (
                      <GaugeTrendsButton
                        component={overallComponent}
                        assessment={assessment}
                        slug={slug}
                        sortedIntervals={sortedIntervals}
                        displayIntervals={parsedIntervalsDisplay}
                        scale={scale}
                        track={handleMixpanelTrack}
                      />
                    )}
                    {result && intro && (
                      <GaugeAboutButtonWithModal
                        data={intro}
                        intervals={numIntervals > 0 && parsedIntervalsDisplay}
                        intervalIndex={intervalIndex}
                        track={handleMixpanelTrack}
                      />
                    )}
                  </div>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                md
                className={ClassNames({
                  [classes.infoWrapper]: true,
                  [classes.infoWrapperInPdfSafari]: isDownloadingPdfInSafari,
                })}
              >
                {overallComponent.resultExpression && (
                  <Typography className={classes.header}>
                    {overallComponent.resultExpression}
                  </Typography>
                )}
                {!isVariantB && numIntervals > 0 && (
                  <Grid
                    container
                    spacing={2}
                    className={classes.intervalContainer}
                  >
                    {parsedIntervalsDisplay.map((interval, i) => (
                      <Grid
                        item
                        xs={6}
                        md={intervalsCol}
                        className={classes.intervalItemWrapper}
                        key={`overall-component-interval-${interval.color}`}
                      >
                        <Box>
                          <Box className={classes.intervalColorWrapper}>
                            <span
                              className={
                                i === intervalIndex
                                  ? classes.actualIntervalColor
                                  : classes.intervalColor
                              }
                              style={{
                                backgroundColor: interval.color,
                                boxShadow:
                                  i === intervalIndex &&
                                  `0 0 8px 0 ${interval.color}`,
                              }}
                            />
                          </Box>
                        </Box>
                        <Box className={classes.intervalInfoWrapper}>
                          <Typography
                            className={classes.intervalValues}
                          >{`${interval.start}-${interval.end}`}</Typography>
                          <Typography className={classes.intervalLabel}>
                            {interval.label}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
                {isVariantB && (
                  <>
                    <Box mt="16px">
                      <RichTextReactRenderer
                        data={_get(
                          result,
                          'fields.descriptionVariantB',
                          _get(result, 'fields.description'),
                        )}
                        paragraphVariant="body2"
                      />
                    </Box>
                    {!invisiblePdfContent && (
                      <Box
                        mt={invisiblePdfContent ? 0 : '16px'}
                        mb={invisiblePdfContent ? 0 : ['16px', '16px', '30px']}
                        ml={invisiblePdfContent ? 0 : [0, 0, '40px']}
                      >
                        <Typography
                          variant="body2"
                          className={classes.descriptionLabel}
                        >
                          <b>
                            {_get(
                              assessmentResultsSiteCopy,
                              'pageCopy.variantBAnchorLinksHelper',
                            )}
                          </b>
                          <Hidden smDown>
                            <>
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={handleSelfHelpResourcesClick}
                              >
                                {_get(
                                  assessmentResultsSiteCopy,
                                  'pageCopy.variantBResultResourcesAnchorLabel',
                                )}
                                &nbsp;↓
                              </span>
                              |
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={handleMemberResourcesClick}
                              >
                                {_get(
                                  assessmentResultsSiteCopy,
                                  'pageCopy.variantBClientResourcesAnchorLabel',
                                ).replace('<userTerm>', userTerm)}
                                &nbsp;↓
                              </span>
                            </>
                          </Hidden>
                        </Typography>
                        <Hidden mdUp>
                          <Typography
                            variant="body2"
                            className={classes.descriptionLabel}
                          >
                            <span
                              role="button"
                              tabIndex={0}
                              className={classes.descriptionLink}
                              onClick={handleSelfHelpResourcesClick}
                              gutterBottom
                            >
                              {_get(
                                assessmentResultsSiteCopy,
                                'pageCopy.variantBResultResourcesAnchorLabel',
                              )}{' '}
                              ↓
                            </span>
                          </Typography>
                          <Typography
                            variant="body2"
                            className={classes.descriptionLabel}
                          >
                            <span
                              role="button"
                              tabIndex={0}
                              className={classes.descriptionLink}
                              onClick={handleMemberResourcesClick}
                              gutterBottom
                            >
                              {_get(
                                assessmentResultsSiteCopy,
                                'pageCopy.variantBClientResourcesAnchorLabel',
                              )}{' '}
                              ↓
                            </span>
                          </Typography>
                        </Hidden>
                      </Box>
                    )}
                  </>
                )}
                {isVariantB && !invisiblePdfContent && (
                  <Box className={classes.signUpButtonWrapperVariantB}>
                    {!hideSignUpButton && (
                      <GaugeSignUpButton
                        assessment={assessment}
                        variant="contained"
                        tight
                      />
                    )}
                    <RetakeButton
                      tight
                      variant={hideSignUpButton ? 'contained' : 'outlined'}
                      trackingProps={{
                        placement: 'top',
                        assessmentName: name,
                        assessmentId: _get(assessment, 'sys.id'),
                      }}
                    />
                  </Box>
                )}
                {!isVariantB && !hideSignUpButton && (
                  <Box className={classes.signUpButtonWrapper}>
                    <GaugeSignUpButton assessment={assessment} />
                  </Box>
                )}
              </Grid>
              {!isVariantB && <Hidden mdUp>{inCrisisComponent}</Hidden>}
            </Grid>
          </Grid>
          {isVariantB && inCrisisComponent}
          {!isVariantB && intro && !invisiblePdfContent && (
            <Grid item xs={12}>
              <GaugeAboutButton data={intro} track={handleMixpanelTrack} />
            </Grid>
          )}
        </>
      ) : (
        intro && (
          <Grid item xs={12}>
            <RichTextReactRenderer data={intro} paragraphVariant="body2" />
          </Grid>
        )
      )}
      {!isVariantB && result && !invisiblePdfContent && (
        <>
          <Grid item xs={12} md className={classes.descriptionWrapper}>
            <RichTextReactRenderer
              data={_get(result, 'fields.description')}
              paragraphVariant="body2"
            />
          </Grid>
          <TrendsWrapper
            type="overall"
            component={overallComponent}
            assessment={assessment}
            slug={slug}
            sortedIntervals={sortedIntervals}
            displayIntervals={parsedIntervalsDisplay}
            scale={scale}
          />
        </>
      )}
      <Grid item xs={12}>
        <PageIntroBlock
          id={_get(result, 'sys.id')}
          page="assessment-results"
          type="reference"
        />
      </Grid>
      {!hideResources && !_isEmpty(resources) && !invisiblePdfContent && (
        <Grid item xs={12} className={classes.resourcesContainer}>
          <ResultList
            assessment={name}
            entryId={_get(result, 'sys.id')}
            resources={resources}
            topics={topics}
            isFullWidth={isFullWidth}
          />
        </Grid>
      )}
    </Grid>
  );
}

OverallComponent.propTypes = {
  overallComponent: PropTypes.object,
  intro: PropTypes.object,
  inCrisisComponent: PropTypes.node,
  userData: PropTypes.shape({
    gender: PropTypes.string,
    age: PropTypes.string,
  }),
  isFullWidth: PropTypes.bool,
  assessment: PropTypes.object,
  hideResources: PropTypes.bool,
};

export default OverallComponent;
